'use client';

import { useAuth } from '@/providers/auth-provider';
import {
  RecommEventPayload,
  uploadNewsEvents,
} from '@/app/services/news-recomm';
import { useCallback, useEffect, useRef } from 'react';

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 10;
const NEWS_RECOMM_ENABLED = process.env.NEXT_PUBLIC_NEWS_RECOMM_ENABLED === 'true';

/**
 * Hook that batches news behavior events and flushes them periodically.
 *
 * - Queue is per-component instance; typically mounted once in the news layout
 * - Skips all tracking when the user is not logged in
 * - On page unload: attempts to flush via sendBeacon; falls back to sync fetch
 */
export function useNewsEventTracker() {
  const { user } = useAuth();
  const isLoggedIn = !!user && NEWS_RECOMM_ENABLED;

  const queueRef = useRef<RecommEventPayload[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flush = useCallback(async () => {
    const batch = queueRef.current;
    if (!batch.length) return;
    queueRef.current = [];

    try {
      await uploadNewsEvents(batch);
    } catch (err) {
      // Events are best-effort; log and discard
      console.warn('[event-tracker] flush failed:', err);
    }
  }, []);

  // Periodic timer
  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      void flush(); // final flush on unmount
    };
  }, [flush]);

  // Page unload: try sendBeacon, fall back to sync
  useEffect(() => {
    const handleUnload = () => {
      const batch = queueRef.current;
      if (!batch.length) return;
      queueRef.current = [];

      const payload = JSON.stringify({ events: batch });
      const sent = navigator.sendBeacon?.(
        '/api/news/events',
        new Blob([payload], { type: 'application/json' }),
      );
      if (!sent) {
        // sendBeacon failed or not available; fire-and-forget fetch
        fetch('/api/news/events', {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const enqueue = useCallback(
    (event: RecommEventPayload) => {
      if (!isLoggedIn) return;
      queueRef.current.push(event);
      if (queueRef.current.length >= FLUSH_THRESHOLD) {
        void flush();
      }
    },
    [isLoggedIn, flush],
  );

  const trackSearch = useCallback(
    (query: string) => {
      enqueue({ event_type: 'search', properties: { query } });
    },
    [enqueue],
  );

  const trackView = useCallback(
    (itemId: string, recommId?: string) => {
      enqueue({ event_type: 'view', item_id: itemId, recomm_id: recommId });
    },
    [enqueue],
  );

  const trackClick = useCallback(
    (itemId: string, recommId?: string) => {
      enqueue({ event_type: 'click', item_id: itemId, recomm_id: recommId });
    },
    [enqueue],
  );

  const trackDetailView = useCallback(
    (itemId: string, recommId?: string) => {
      enqueue({
        event_type: 'detail_view',
        item_id: itemId,
        recomm_id: recommId,
      });
    },
    [enqueue],
  );

  const trackFavorite = useCallback(
    (itemId: string) => {
      enqueue({ event_type: 'favorite', item_id: itemId });
    },
    [enqueue],
  );

  const trackShare = useCallback(
    (itemId: string) => {
      enqueue({ event_type: 'share', item_id: itemId });
    },
    [enqueue],
  );

  const trackLike = useCallback(
    (itemId: string) => {
      enqueue({ event_type: 'like', item_id: itemId });
    },
    [enqueue],
  );

  return {
    trackSearch,
    trackView,
    trackClick,
    trackDetailView,
    trackFavorite,
    trackShare,
    trackLike,
    flushEvents: flush,
  };
}
